import { LandingPageAgentSession } from '@superfunnel/types';
import { doc, setDoc } from 'firebase/firestore';
import { useCallback, useEffect } from 'react';
import { db } from '../Firebase';
import { AgentChange } from '../types/agentChanges';
import { useBaseAgentSession } from './useBaseAgentSession';

interface AgentContext {
  websiteId: string;
  dynamicPageId?: string;
  setIsEditable: (editable: boolean) => void;
}

export interface UseLandingPageAgentSessionReturn {
  agentChanges: AgentChange[];
  agentHasUnreviewedChanges: boolean;
  agentUpdateChanges: (
    changeUpdates: {
      id: string;
      status: 'accepted' | 'rejected';
    }[],
  ) => Promise<void>;
  agentUpdateWebsiteData: (websiteData: unknown) => Promise<void>;
  agentSessionData: LandingPageAgentSession | null;
}

/**
 * Hook to listen to landing page agent session changes in Firestore
 * Converts agent-specific change format to generic AgentChange format
 * Gets threadId from AgentChatContext
 * @param {AgentContext | null} context - Agent context containing websiteId and dynamicPageId
 * @return {UseLandingPageAgentSessionReturn} Object with changes, session data, and change management functions
 */
export function useLandingPageAgentSession(
  context: AgentContext | null,
): UseLandingPageAgentSessionReturn {
  const baseSession = useBaseAgentSession<LandingPageAgentSession>({
    // Only enable when we have the required context
    enabled: !!context?.websiteId,

    // Title generation for section changes
    getTitleForChange: (change, sessionData) => {
      const section = sessionData?.websiteData?.sections?.find(
        (s) => s.id === change.entityId,
      );
      const getSectionName = () => {
        if (!section) {
          return change.sectionName || change.sectionType || 'section';
        }
        return section?.name ? `"${section.name}"` : section?.type || 'section';
      };
      switch (change.type) {
        case 'created':
          return `Created ${getSectionName()} section`;
        case 'modified':
          return `Modified ${getSectionName()} section`;
        case 'deleted':
          return `Deleted ${getSectionName()} section`;
        default:
          return `Change in ${change?.entityId}`;
      }
    },

    // Sort changes by section order in website data
    sortChanges: (changes, sessionData) => {
      const sectionsOrderMap = sessionData?.websiteData?.sections
        ? new Map<string, number>(
            sessionData.websiteData.sections.map((s, index) => [s.id, index]),
          )
        : null;

      if (!sectionsOrderMap) return changes;

      return [...changes].sort((a, b) => {
        const orderA =
          sectionsOrderMap.get(a.entityId) ?? Number.MAX_SAFE_INTEGER;
        const orderB =
          sectionsOrderMap.get(b.entityId) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    },
  });

  // Control editable state based on session status and unreviewed changes
  useEffect(() => {
    const { setIsEditable } = context || {};
    if (!setIsEditable) return;
    setIsEditable(
      baseSession.sessionStatus !== 'active' &&
        !baseSession.agentHasUnreviewedChanges,
    );
  }, [
    baseSession.sessionStatus,
    baseSession.agentHasUnreviewedChanges,
    context,
  ]);

  // Function to update website data
  const updateWebsiteData = useCallback(
    async (websiteData: unknown): Promise<void> => {
      const { sessionDocPath, agentSessionData } = baseSession;

      if (!sessionDocPath || !agentSessionData) {
        return;
      }

      const sessionDocRef = doc(db, sessionDocPath);

      await setDoc(
        sessionDocRef,
        { websiteData },
        { mergeFields: ['websiteData'] },
      );
    },
    [baseSession],
  );

  return {
    agentChanges: baseSession.agentChanges,
    agentHasUnreviewedChanges: baseSession.agentHasUnreviewedChanges,
    agentUpdateChanges: baseSession.agentUpdateChanges,
    agentUpdateWebsiteData: updateWebsiteData,
    agentSessionData: baseSession.agentSessionData,
  };
}
